import clsx from "clsx";
import { WORKBENCH_SEARCH_PILL_CLASS } from "@/components/ui/workbench/workbenchSurface";
import {
  ADMIN_BANNER,
  ADMIN_BANNER_BTN,
  ADMIN_BANNER_EMPHASIS,
  ADMIN_BANNER_TEXT,
  COUNT_BADGE,
  FILTER_BUTTON,
  FOCUS_RING,
  LINK_DEFAULT,
  LINK_INDIGO,
  LIST_SHELL,
  ROW_HOVER,
  SURFACE_DROPDOWN_PANEL,
  SURFACE_TRANSLUCENT_DARK,
  SURFACE_HOVER,
  SURFACE_ICON_BTN,
  SURFACE_INSET,
  SURFACE_MATRIX_ROW_EVEN,
  SURFACE_MATRIX_ROW_ODD,
  SURFACE_MATRIX_TOOLBAR,
  SURFACE_NOTIFICATION_ROW,
  SURFACE_PAGE,
  SURFACE_RAISED,
  SURFACE_SELECTED,
  SURFACE_SKELETON,
  TEXT_BODY,
  TEXT_HEADING,
  TEXT_HERO,
  TEXT_MUTED,
  TEXT_ROW_SUBTITLE,
  TEXT_ROW_TITLE,
  TEXT_TITLE,
  BORDER_DIVIDER,
  CARD_FOOTER,
  CARD_HEADER,
  CONTEXT_RAIL_WIDTH,
  GAP_INLINE,
  GAP_INLINE_TIGHT,
  GAP_STACK,
  GAP_STACK_LOOSE,
  GAP_SURFACE,
  PAD_BAR,
  PAD_CARD,
  PAD_INSET,
  PAD_ROW,
  RADIUS_CHIP,
  RADIUS_INSET,
  RADIUS_ROW,
  RADIUS_SURFACE,
  SPACE_PAGE,
  SURFACE_ACTION_BAR,
  TEXT_BODY_MUTED,
  TEXT_EYEBROW,
  TEXT_EYEBROW_ACCENT,
  TEXT_META,
  TEXT_STAT,
} from "@/components/ui/surface/surfaceTokens";

/**
 * Home dashboard surfaces — flat elevation via background steps only
 * (aligned with design-system/catalog-surfaces, without drop shadows).
 *
 * Dark: page is transparent (desktop shows through); widgets are frosted glass.
 * Light: page 100 → raised white (unchanged).
 */

export const HOME_SURFACE = clsx(RADIUS_SURFACE, SURFACE_PAGE);

export const HOME_SURFACE_RAISED = clsx(RADIUS_SURFACE, SURFACE_RAISED);

/** Meeting zone inside the header — flat, no shadow, one step above header bg. */
export const HOME_MEETING_CARD = clsx(RADIUS_SURFACE, "bg-slate-100", SURFACE_TRANSLUCENT_DARK);

export const HOME_SURFACE_INSET = clsx(RADIUS_INSET, SURFACE_INSET);

export const HOME_SURFACE_HOVER = SURFACE_HOVER;

/** Matrix toolbar, column header row, and filter footer. */
export const HOME_MATRIX_TOOLBAR = SURFACE_MATRIX_TOOLBAR;

/** Subtle zebra stripe on odd rows (card body is glass / slate-900). */
export const HOME_MATRIX_ROW_ODD = SURFACE_MATRIX_ROW_ODD;

/** Even rows inherit the card surface. */
export const HOME_MATRIX_ROW_EVEN = SURFACE_MATRIX_ROW_EVEN;

/** Selected matrix filter / row highlight. */
export const HOME_FILTER_SELECTED = SURFACE_SELECTED;

export function homeMatrixRowClass(rowIndex: number): string {
  return rowIndex % 2 === 1 ? HOME_MATRIX_ROW_ODD : HOME_MATRIX_ROW_EVEN;
}

export function homeMatrixStickyCellClass(rowIndex: number): string {
  return clsx("sticky left-0 z-10", homeMatrixRowClass(rowIndex));
}

/** Section / widget title on raised home cards. */
export const HOME_TITLE_CLASS = TEXT_TITLE;

/** Hero greeting headline. */
export const HOME_HEADING_CLASS = TEXT_HEADING;

/** Standout lead line under greeting. */
export const HOME_HERO_TEXT_CLASS = TEXT_HERO;

/** Primary body copy on raised cards. */
export const HOME_BODY_TEXT_CLASS = TEXT_BODY;

/** Secondary / helper copy (works in both themes). */
export const HOME_MUTED_TEXT_CLASS = TEXT_MUTED;

/** Row primary label. */
export const HOME_ROW_TITLE_CLASS = TEXT_ROW_TITLE;

/** Row secondary label. */
export const HOME_ROW_SUBTITLE_CLASS = TEXT_ROW_SUBTITLE;

/** Interactive list row hover/focus. */
export const HOME_ROW_HOVER = ROW_HOVER;

/** Count pill beside section titles. */
export const HOME_COUNT_BADGE_CLASS = COUNT_BADGE;

export const HOME_DIVIDER_CLASS = BORDER_DIVIDER;

export const HOME_LIST_SHELL_CLASS = LIST_SHELL;

export const HOME_SKELETON_CLASS = SURFACE_SKELETON;

export const HOME_ICON_BTN_CLASS = SURFACE_ICON_BTN;

export const HOME_LINK_CLASS = LINK_DEFAULT;

export const HOME_LINK_INDIGO_CLASS = LINK_INDIGO;

export const HOME_FILTER_BUTTON_CLASS = clsx(WORKBENCH_SEARCH_PILL_CLASS, FILTER_BUTTON);

export const HOME_DROPDOWN_PANEL_CLASS = SURFACE_DROPDOWN_PANEL;

export const HOME_NOTIFICATION_ROW_CLASS = SURFACE_NOTIFICATION_ROW;

/** Admin “view as” banner above home surfaces. */
export const HOME_ADMIN_BANNER_CLASS = ADMIN_BANNER;

export const HOME_ADMIN_BANNER_TEXT_CLASS = ADMIN_BANNER_TEXT;

export const HOME_ADMIN_BANNER_EMPHASIS_CLASS = ADMIN_BANNER_EMPHASIS;

export const HOME_ADMIN_BANNER_BTN_CLASS = ADMIN_BANNER_BTN;

/** Matrix + side panel when a filter is active — cap height so lists can scroll. */
export const HOME_MATRIX_TASK_LAYOUT_CLASS =
  "flex w-full min-h-0 max-h-[min(70vh,720px)] min-w-0 items-stretch overflow-hidden";

/** Matrix only (no panel) — full width, content height (no stretch gap). */
export const HOME_MATRIX_LAYOUT_CLASS = "flex w-full min-w-0 items-start overflow-hidden";

// ── Layout tokens (aliases) ─────────────────────────────────────────────────

export const HOME_SPACE_PAGE = SPACE_PAGE;

export const HOME_GAP_SURFACE = GAP_SURFACE;

export const HOME_PAD_CARD = PAD_CARD;

export const HOME_PAD_INSET = PAD_INSET;

export const HOME_PAD_BAR = PAD_BAR;

export const HOME_PAD_ROW = PAD_ROW;

export const HOME_GAP_STACK = GAP_STACK;

export const HOME_GAP_STACK_LOOSE = GAP_STACK_LOOSE;

export const HOME_GAP_INLINE = GAP_INLINE;

export const HOME_GAP_INLINE_TIGHT = GAP_INLINE_TIGHT;

export const HOME_CARD_HEADER = CARD_HEADER;

export const HOME_CARD_FOOTER = CARD_FOOTER;

export const HOME_EYEBROW_CLASS = TEXT_EYEBROW;

export const HOME_EYEBROW_ACCENT_CLASS = TEXT_EYEBROW_ACCENT;

export const HOME_META_TEXT_CLASS = TEXT_META;

export const HOME_BODY_MUTED_CLASS = TEXT_BODY_MUTED;

export const HOME_STAT_VALUE_CLASS = TEXT_STAT;

export const HOME_CONTEXT_RAIL_WIDTH = CONTEXT_RAIL_WIDTH;

export const HOME_ACTION_BAR_CLASS = SURFACE_ACTION_BAR;
